import { useState, useMemo, useCallback } from 'react'
import { 
  Eye, 
  Download, 
  Share, 
  Edit, 
  Trash2, 
  MoreVertical, 
  Image, 
  Video, 
  Music, 
  FileText,
  Shield,
  Globe
} from 'lucide-react'
import { formatFileSize, formatDate } from '@/lib/utils'
import { ContentItem } from '@/types'
import { analyticsService } from '@/services/analyticsService'
import toast from 'react-hot-toast'

interface ContentGridProps {
  content: ContentItem[]
  onEdit?: (content: ContentItem) => void
  onDelete?: (contentId: string) => void
  onView?: (content: ContentItem) => void
  showActions?: boolean
  variant?: 'grid' | 'list'
}

const ContentGrid: React.FC<ContentGridProps> = ({
  content,
  onEdit,
  onDelete,
  onView,
  showActions = true,
  variant = 'grid'
}) => {
  const [selectedContent, setSelectedContent] = useState<ContentItem | null>(null)
  const [showMenu, setShowMenu] = useState<string | null>(null)

  // Memoized content with computed properties
  const processedContent = useMemo(() => {
    return content.map(item => ({
      ...item,
      displayName: item.metadata?.title || item.originalFilename,
      fileType: getFileType(item.metadata?.mimeType || ''),
      isProtected: item.metadata?.fingerprint ? true : false,
      hasWatermark: item.metadata?.watermarkData ? true : false
    }))
  }, [content])

  const getFileType = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType.startsWith('video/')) return 'video'
    if (mimeType.startsWith('audio/')) return 'audio'
    return 'document'
  }

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'image': return Image
      case 'video': return Video
      case 'audio': return Music
      default: return FileText
    }
  }

  const handleView = useCallback(async (content: ContentItem) => {
    try {
      await analyticsService.trackView(content.id)
      onView?.(content)
    } catch (error) {
      console.error('Failed to track view:', error)
    }
  }, [onView])

  const handleDownload = useCallback(async (content: ContentItem) => {
    try {
      await analyticsService.trackDownload(content.id)
      // Create download link
      const downloadUrl = content.downloadUrl || content.url
      if (!downloadUrl) {
        toast.error('Download URL not available')
        return
      }
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = content.originalFilename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success('Download started')
    } catch (error) {
      toast.error('Download failed')
    }
  }, [])

  const handleShare = useCallback(async (content: ContentItem) => {
    try {
      await analyticsService.trackShare(content.id)
      if (navigator.share) {
        const shareUrl = content.url
        if (!shareUrl) {
          toast.error('Share URL not available')
          return
        }
        await navigator.share({
          title: content.metadata?.title || content.originalFilename,
          text: content.metadata?.description || '',
          url: shareUrl
        })
      } else {
        // Fallback to clipboard
        const shareUrl = content.url
        if (!shareUrl) {
          toast.error('Share URL not available')
          return
        }
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Link copied to clipboard')
      }
    } catch (error) {
      toast.error('Share failed')
    }
  }, [])

  const handleDelete = useCallback(async (contentId: string) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      onDelete?.(contentId)
    }
  }, [onDelete])

  if (variant === 'list') {
    return (
      <div className="space-y-2">
        {processedContent.map((item) => {
          const Icon = getFileIcon(item.fileType)
          return (
            <div
              key={item.id}
              className="flex items-center p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Icon className="h-6 w-6 text-gray-400" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {item.displayName}
                  </h3>
                  {item.isProtected && (
                    <Shield className="h-4 w-4 text-blue-500" />
                  )}
                  {item.hasWatermark && (
                    <Globe className="h-4 w-4 text-green-500" />
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {formatFileSize(item.metadata?.size || 0)} • {item.fileType}
                </p>
                <p className="text-xs text-gray-400">
                  Uploaded {formatDate(item.createdAt)}
                </p>
              </div>

              {showActions && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleView(item)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="View"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDownload(item)}
                    className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleShare(item)}
                    className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                    title="Share"
                  >
                    <Share className="h-4 w-4" />
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(showMenu === item.id ? null : item.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {showMenu === item.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                        <div className="py-1">
                          {onEdit && (
                            <button
                              onClick={() => {
                                onEdit(item)
                                setShowMenu(null)
                              }}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => {
                                handleDelete(item.id)
                                setShowMenu(null)
                              }}
                              className="flex items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {processedContent.map((item) => {
        const Icon = getFileIcon(item.fileType)
        return (
          <div
            key={item.id}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden"
          >
            <div className="aspect-video bg-gray-100 relative group">
              {item.metadata?.image ? (
                <img
                  src={item.metadata.image}
                  alt={item.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon className="h-12 w-12 text-gray-400" />
                </div>
              )}
              
              {/* Overlay with actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                  <button
                    onClick={() => handleView(item)}
                    className="p-2 bg-white rounded-full shadow hover:bg-gray-50 transition-colors"
                    title="View"
                  >
                    <Eye className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleDownload(item)}
                    className="p-2 bg-white rounded-full shadow hover:bg-gray-50 transition-colors"
                    title="Download"
                  >
                    <Download className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => handleShare(item)}
                    className="p-2 bg-white rounded-full shadow hover:bg-gray-50 transition-colors"
                    title="Share"
                  >
                    <Share className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Protection badges */}
              <div className="absolute top-2 right-2 flex space-x-1">
                {item.isProtected && (
                  <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                    <Shield className="h-3 w-3" />
                  </div>
                )}
                {item.hasWatermark && (
                  <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                    <Globe className="h-3 w-3" />
                  </div>
                )}
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {item.displayName}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatFileSize(item.metadata?.size || 0)} • {item.fileType}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(item.createdAt)}
                  </p>
                </div>

                {showActions && (
                  <div className="relative">
                    <button
                      onClick={() => setShowMenu(showMenu === item.id ? null : item.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {showMenu === item.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                        <div className="py-1">
                          {onEdit && (
                            <button
                              onClick={() => {
                                onEdit(item)
                                setShowMenu(null)
                              }}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </button>
                          )}
                          {onDelete && (
                            <button
                              onClick={() => {
                                handleDelete(item.id)
                                setShowMenu(null)
                              }}
                              className="flex items-center px-4 py-2 text-sm text-red-700 hover:bg-red-50 w-full"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ContentGrid 